import React from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector, useStore } from "react-redux";
import { Listbox, Transition } from "@headlessui/react";
import { Check, ChevronDown, Globe } from "lucide-react";
import { AdminUserAgent } from "../../../../../../api/adminUserAgent";
import { getTokenFromStorage } from "../../../../../../utils";
import { setLanguage, setUserInitialConfiguration } from "../../../../../../reducers/user";
import useSettingsSave from "../../../../../pages/Settings/use-settings-save";
import SettingsSaveStatus from "../../../../../pages/Settings/components/save-status";
import LanguageFlag from "../../../../../company/components/language-flag";

const LANGUAGES = [
    { value: "nl", label: "Nederlands" },
    { value: "en", label: "English" }
];

const LanguageSelector = () => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const store = useStore();
    const language = useSelector((state) => state.userInformationReducer.configuration.language || "nl");
    const saving = useSettingsSave();

    const changeLanguage = (value) => saving.save("language", async () => {
        await AdminUserAgent.updateCurrentUser(getTokenFromStorage(), { language: value });
        dispatch(setLanguage(value));
        const { initialConfiguration } = store.getState().userInformationReducer;
        dispatch(setUserInitialConfiguration({ ...initialConfiguration, language: value }));
    });

    return (
        <div className="relative">
            <Listbox value={language} onChange={changeLanguage} disabled={saving.status === "saving"}>
                {({ open }) => (
                    <>
                        <Listbox.Button aria-label={intl.formatMessage({ id: "teamroom.settings.language" })} className="flex items-center gap-1.5 text-standart-ocean rounded-full bg-[#eef3f8] px-3 h-9 text-[13px] font-bold hover:brightness-95 transition disabled:opacity-50">
                            <Globe size={15} aria-hidden />
                            <span>{language.toUpperCase()}</span>
                            <ChevronDown size={15} strokeWidth={2.4} aria-hidden className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
                        </Listbox.Button>
                        <Transition show={open} as={React.Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                            <Listbox.Options className="absolute right-0 z-30 w-48 mt-2 rounded-[18px] bg-white border border-gray-100 shadow-custom-sm p-1.5">
                                {LANGUAGES.map((option) => (
                                    <Listbox.Option key={option.value} value={option.value} className={({ active, selected }) => `flex items-center gap-3 rounded-[14px] px-3.5 py-2.5 text-[13px] font-bold cursor-pointer select-none transition ${selected ? "bg-standart-ocean/10 text-standart-ocean" : active ? "bg-[#eef3f8] text-navy-blue" : "text-navy-blue"}`}>
                                        {({ selected }) => (
                                            <>
                                                <LanguageFlag language={option.value} />
                                                <span className="flex-1 text-left">{option.label}</span>
                                                {selected && <Check size={14} strokeWidth={2.6} aria-hidden className="shrink-0" />}
                                            </>
                                        )}
                                    </Listbox.Option>
                                ))}
                            </Listbox.Options>
                        </Transition>
                    </>
                )}
            </Listbox>
            {saving.error && <div className="absolute right-0 top-full mt-2 bg-white rounded-xl p-2 shadow-custom-sm min-w-max max-w-[260px]"><SettingsSaveStatus {...saving} /></div>}
        </div>
    );
};

export default LanguageSelector;
